import {
  DataProvider,
  Filtering,
  QDataTable,
  QDataTableActionBar,
  useCurrentUser,
} from '@qualio/ui-components';
import { useContext } from 'react';
import { QueryClient } from 'react-query';
import { documentApi } from '../../../../../../api/document';
import { QualioDocument } from '../../../../../../api/model/document';
import { BulkActionsWrapper } from '../../BulkActionsV2/BulkActionWrapper';
import { getTableParams, TableFilter } from '../../Filter/TableFilters';
import { useWorkspaceTableColumns } from '../../hooks/useWorkspaceTableColumns';
import { WorkspaceTabsContext } from '../../WorkspaceTabsContext';
import { WorkspaceTabs } from '../../WorkspaceV2';

const buildQueryKey = (companyId?: number) => [
  'your-actions',
  Number(companyId),
];

export const invalidateYourActionsTableData = async (
  queryClient: QueryClient,
  companyId: number,
) => {
  await queryClient.invalidateQueries(buildQueryKey(companyId));
};

export const YourActionsTable = () => {
  const currentUser = useCurrentUser();
  const { setIsReadyForExport } = useContext(WorkspaceTabsContext);
  const getActionableDocuments = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<QualioDocument>> => {
    setIsReadyForExport(false);
    const {
      offset,
      limit,
      orderBy,
      searchTerm,
      templateFilter,
      actionFilters,
      tagsFilter,
      ownerFilter,
    } = getTableParams(pageParam);
    const apiMethod = actionFilters.includes('for_training')
      ? documentApi.fetchUserTrainingDocs
      : documentApi.fetchUserActionableDocs;

    const { total: itemCount, documents: data } = await apiMethod(
      currentUser,
      actionFilters,
      searchTerm,
      tagsFilter,
      offset * limit,
      limit,
      orderBy,
      templateFilter,
      ownerFilter,
    );

    if (itemCount > 0 && data?.length > 0) {
      setIsReadyForExport(true);
    }

    return {
      itemCount,
      data: data || [],
    };
  };

  const columns = useWorkspaceTableColumns();

  return (
    <DataProvider.Remote
      queryKey={buildQueryKey(currentUser.companyId)}
      queryFn={getActionableDocuments}
      queryParamKeys={{
        searchTerm: 'query',
        pageIndex: 'offset',
        pageSize: 'limit',
      }}
    >
      <QDataTable
        data-cy="workspace-your-actions-table"
        columns={columns}
        getRowId={(row) => row.id.toString()}
      >
        <Filtering.FormContent>
          <TableFilter tab={WorkspaceTabs.YOUR_ACTIONS} />
        </Filtering.FormContent>
        <QDataTableActionBar>
          <BulkActionsWrapper currentTab={WorkspaceTabs.YOUR_ACTIONS} />
        </QDataTableActionBar>
      </QDataTable>
    </DataProvider.Remote>
  );
};
